import { KeyboardEvent, useEffect, useId, useRef } from 'react';

import Button from 'components/ui/button';

interface ConfirmarEncerramentoProps {
  onEncerrar: () => void;
  onVoltar: () => void;
}

/**
 * Confirmação de encerramento sobre a janela do chat (CHAT-23): o cartão branco com borda rosa da
 * casca dos modais (`appearance="cartao"`). É modal, como o padrão ARIA pede para o `alertdialog`: o foco
 * começa em "Voltar ao chat", a saída sem perda, e o Tab circula entre os dois botões.
 */
export default function ConfirmarEncerramento({ onEncerrar, onVoltar }: ConfirmarEncerramentoProps) {
  const tituloId = useId();
  const textoId = useId();
  const voltarRef = useRef<HTMLButtonElement>(null);
  const encerrarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => voltarRef.current?.focus(), []);

  function prenderOFoco(evento: KeyboardEvent) {
    if (evento.key !== 'Tab') return;
    const [primeiro, ultimo] = [voltarRef.current, encerrarRef.current];
    const saindo = evento.shiftKey ? document.activeElement === primeiro : document.activeElement === ultimo;
    if (!saindo) return;
    evento.preventDefault();
    (evento.shiftKey ? ultimo : primeiro)?.focus();
  }

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center p-5">
      <div aria-hidden="true" className="absolute inset-0 rounded-jj-2xl bg-brand-navy opacity-60" />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        aria-describedby={textoId}
        onKeyDown={prenderOFoco}
        className="relative flex w-full max-w-[360px] flex-col items-center gap-3 rounded-jj-2xl border-4 border-solid border-brand-pink-light bg-brand-white px-6 py-7 text-center shadow-jj-modal-brand"
      >
        <h3 id={tituloId} className="m-0 font-display text-[19px] font-bold leading-snug text-brand-blue-dark">
          Deseja mesmo encerrar esse chat?
        </h3>
        <p id={textoId} className="m-0 text-sm leading-normal text-content">
          Você perderá todo o histórico da sua interação com o JJ.
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-2.5">
          <Button ref={voltarRef} variant="secundario" size="sm" onClick={onVoltar}>
            Voltar ao chat
          </Button>
          <Button ref={encerrarRef} variant="principal" size="sm" onClick={onEncerrar}>
            Encerrar chat
          </Button>
        </div>
      </div>
    </div>
  );
}
